import React, { useEffect, useMemo } from "react";
import { Dropdown, Typography, Flex } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ConnectedProps, connect } from "react-redux";
import { ReducerState, UserOrganization } from "../../../types";
import { getUserOrganizations } from "../../../actions";
import { useWebServer } from "../../../providers";
import { getActiveOrg, setActiveOrg } from "../../../utils/activeOrg";

const { Title } = Typography;

const mapStateToProps = (state: ReducerState) => ({
  userOrganizations: state.userOrganizations,
});

const connector = connect(mapStateToProps, { getUserOrganizations });

type Props = ConnectedProps<typeof connector>;

/**
 * Organization picker.
 *
 * The list comes from GET /organizations, which answers "which organizations may
 * this caller act as, in Scout". That is a backend question: the memberships on
 * the token carry only slugs, the display names live in Scout's database, and a
 * superuser's list - any organization - is not on the token at all. Asking the
 * backend also means the picker can never offer something the next request would
 * be refused for.
 *
 * Shown only when there is more than one to choose between; a single
 * organization is not a choice, and rendering a dropdown for it would be noise.
 */
const OrgSelector: React.FC<Props> = ({
  userOrganizations,
  getUserOrganizations,
}) => {
  const { sendRequest } = useWebServer();

  useEffect(() => {
    getUserOrganizations(sendRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = getActiveOrg();

  const current = useMemo(
    () =>
      userOrganizations.find((o: UserOrganization) => o.uuid === active) ||
      userOrganizations.find((o: UserOrganization) => o.active),
    [userOrganizations, active],
  );

  const onSwitch = (uuid: string) => {
    if (!uuid || uuid === active) return;

    setActiveOrg(uuid);

    // A full reload rather than clearing state by hand. Scout holds several
    // independent RTK Query caches plus filter and selection state, none of
    // which reset on their own; clearing them piecemeal means finding every one
    // and a missed one is silent - the user would be left looking at the
    // previous organization's captures.
    window.location.reload();
  };

  const items: MenuProps["items"] = useMemo(
    () =>
      userOrganizations.map((org: UserOrganization) => ({
        key: org.uuid,
        label: org.display_name || org.uuid,
        onClick: () => onSwitch(org.uuid),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userOrganizations, active],
  );

  if (userOrganizations.length < 2) return null;

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
      <Flex
        align="center"
        gap={8}
        style={{ cursor: "pointer" }}
        justify={"center"}
      >
        <Title level={5} style={{ margin: 0 }}>
          {current?.display_name || active || "Select organization"}
        </Title>
        <DownOutlined style={{ fontSize: 10 }} />
      </Flex>
    </Dropdown>
  );
};

export default connector(OrgSelector);
